import { useMemo } from "react";
import { type Application, type Shift } from "@/lib/backend/api";
import { useMyApplications } from "@/lib/query/application/use-my-applications";
import { useMyShifts } from "@/lib/query/shift/use-my-shifts";
import { createDayProjectKey } from "@/utils/time";

/**
 * State machine for upcoming shifts overview
 *
 * Logic:
 *
 * - For each application, check if there's a confirmed shift on the same project on the same day
 * - If yes, show only the shift (application is consumed/converted)
 * - If no, show the application
 *
 * This creates a unified view where users see either their applications OR their shifts, never both
 * for the same day/project combination.
 */

/**
 * Een dienst zoals dit overzicht hem oplevert.
 *
 * `startAt` en `projectId` staan er gegarandeerd op, want daar filtert de hook op: zonder dag en
 * zonder project valt er geen kaart te tekenen. Verder is het een gewone `Shift`, en dus blijft
 * `shiftTimeEntry` optioneel.
 *
 * Hier stond `shiftTimeEntry: ShiftTimeEntry` bij terwijl de filter dat veld nooit controleerde.
 * Zolang de hook diensten met uren erop wegfilterde viel dat niet op. Sinds die filter weg is (zie
 * de uitleg bij `shiftItems` hieronder) staan diensten met en zonder tijdregistratie hier naast
 * elkaar, en beloofde het type iets wat de helft van de rijen niet waarmaakt. Een consument die
 * `shift.shiftTimeEntry.status` las kwam schoon door de typecheck en viel om op een toestel.
 */
export type UpcomingShift = Shift & { startAt: string; projectId: string };

export type UpcomingShiftItem =
  | {
      type: "shift";
      shift: UpcomingShift;
      date: Date;
      projectId: string;
      sortKey: string;
    }
  | {
      type: "application";
      application: Application;
      date: Date;
      projectId: string;
      sortKey: string;
    };

export function useUpcomingShiftsOverview() {
  const {
    applications,
    loadMore: loadMoreApplications,
    hasNextPage: hasNextPageApplications,
    refetch: refetchApplications,
    isLoading: isLoadingApplications,
  } = useMyApplications();

  const {
    shifts,
    loadMore: loadMoreShifts,
    hasNextPage: hasNextPageShifts,
    refetch: refetchShifts,
    isLoading: isLoadingShifts,
  } = useMyShifts();

  /**
   * Merge and deduplicate applications and shifts Priority: Shifts > Applications (if both exist
   * for same day + project)
   */
  const items = useMemo<UpcomingShiftItem[]>(() => {
    // Create a map of shifts by day + project, including those with endAt
    const shiftsByDayAndProject = new Map<string, Shift>();

    shifts
      .filter((shift): shift is UpcomingShift => Boolean(shift?.startAt && shift?.projectId))
      .forEach((shift) => {
        const dayKey = createDayProjectKey(
          shift.startAt,
          shift.projectId,
          shift.timeZone ?? shift.project?.timeZone,
        );
        shiftsByDayAndProject.set(dayKey, shift);
      });

    // Process applications and filter out those with matching shifts or shifts with endAt
    const applicationItems: UpcomingShiftItem[] = [];

    applications.forEach((application) => {
      // Skip CANCELLATION_REQUESTED applications - this info is shown on the shift card instead
      if (application.status === "CANCELLATION_REQUESTED") {
        return;
      }

      // CANCELLED applications are always shown (no shift overlap possible)
      if (application.status === "CANCELLED") {
        const slot = application.availabilitySlot;
        if (slot?.startAt && slot?.project?.id) {
          applicationItems.push({
            type: "application",
            application,
            date: new Date(slot.startAt),
            projectId: slot.project.id,
            sortKey: slot.startAt,
          });
        }
        return;
      }

      const slot = application.availabilitySlot;
      if (!slot?.startAt || !slot?.project?.id) {
        return;
      }

      const dayKey = createDayProjectKey(slot.startAt, slot.project.id, slot.project.timeZone);

      // If there's a shift for this day + project, skip the application
      if (shiftsByDayAndProject.has(dayKey)) {
        return;
      }

      applicationItems.push({
        type: "application",
        application,
        date: new Date(slot.startAt),
        projectId: slot.project.id,
        sortKey: slot.startAt,
      });
    });

    /**
     * Alleen diensten met de gegevens die de kaart nodig heeft.
     *
     * Bewust géén filter op "uren al ingediend". Dat stond hier wel, en het maakte een gat:
     * `useMyShifts` levert alleen diensten met `endAt >= nu`, dus een dienst die nog bezig is stond
     * hier — tot je je uren indiende. Dan viel hij uit dit overzicht, terwijl Afgerond hem nog niet
     * toonde (die eist `endAt < nu`). De dienst was daarmee nergens meer te zien.
     *
     * Dezelfde fout is op 13-08-2026 al weggehaald uit `use-separated-shifts-and-applications.ts`.
     * Organisaties met `separationBetweenShiftsAndApplicationsInApp` uit draaien deze hook en
     * hadden hem dus nog.
     *
     * De queries zijn precies complementair (`endAt >= nu` hier, `endAt < nu` bij Afgerond). Elke
     * extra conditie aan één kant breekt dat. Een dienst blijft hier dus staan tot zijn geplande
     * eindtijd voorbij is en Afgerond hem overneemt — ook als er al uren op staan.
     *
     * De dedup van aanmeldingen hierboven kijkt alleen of er een dienst op dezelfde dag en
     * hetzelfde project staat. Er stond een tweede check op "dienst met een eindtijd", maar die kon
     * per constructie nooit vuren: elke dienst gaat onvoorwaardelijk in `shiftsByDayAndProject` met
     * dezelfde sleutel, en de check daarop returnt twee regels eerder. Die is weg, met
     * `hasClockedOut` erbij, want daarmee had die helper geen gebruiker meer.
     */
    const shiftItems: UpcomingShiftItem[] = shifts
      .filter((shift): shift is UpcomingShift => Boolean(shift?.startAt && shift?.projectId))
      .map((shift) => ({
        type: "shift" as const,
        shift,
        date: new Date(shift.startAt),
        projectId: shift.projectId,
        sortKey: shift.startAt,
      }));

    // Combine and sort by date
    return [...applicationItems, ...shiftItems].sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }, [applications, shifts]);

  // Combined refetch function
  const refetch = async () => {
    await Promise.all([refetchApplications(), refetchShifts()]);
  };

  // Combined load more function
  const loadMore = async (ev: CustomEvent) => {
    await Promise.all([
      hasNextPageApplications ? loadMoreApplications(ev) : Promise.resolve(),
      hasNextPageShifts ? loadMoreShifts(ev) : Promise.resolve(),
    ]);
    if (ev.target) {
      (ev.target as any).complete();
    }
  };

  const hasNextPage = hasNextPageApplications || hasNextPageShifts;

  return {
    items,
    loadMore,
    hasNextPage,
    refetch,
    /**
     * True only on the very first load of either source. Consumers need this to tell "you have
     * nothing planned" apart from "we don't know yet" — showing the first while the second is true
     * is how an app tells a worker they are unemployed for half a second.
     */
    isLoading: isLoadingApplications || isLoadingShifts,
  };
}
